/**
 * Main Application Component
 *
 * Sets up React Router with the following routes:
 * - /login: WordPress SSO login page (unprotected)
 * - /workbooks: Main workbook list (protected)
 * - /workbook/:id: Workbook editor page (protected)
 * - /passages: Passage library management (protected)
 * - /load-questions: Load questions from Excel files (protected)
 * - /editor: Passage editor for text processing (protected)
 * - *: 404 Not Found page
 *
 * Wraps the entire app with AuthProvider for global authentication state.
 * Protected routes require valid JWT token to access.
 */

import { type CSSProperties, type ReactNode, Suspense, useEffect, useRef } from 'react';
import { AdminModeBar } from '@/components/layout/AdminModeBar';
import { NewVersionBanner } from '@/components/layout/NewVersionBanner';
import { SitePopup } from '@/components/popups/SitePopup';
import { MarketingConsentPrompt } from '@/components/auth/MarketingConsentPrompt';
import { useImpersonationActive } from '@/hooks/useImpersonationActive';
import { SettingsInitializer } from '@/components/providers/SettingsInitializer';
import { lazyWithRetry as lazy } from '@/lib/lazyWithRetry';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { OneClickMocktestRedirect } from '@/components/oneclick/OneClickMocktestRedirect';
import { AuthProvider } from '@/context/AuthContext';
import { ToastProvider } from '@/contexts/ToastContext';
import { QuestionGenerationProvider } from '@/contexts/QuestionGenerationContext';
import { GlobalProgressWidget } from '@/components/common/GlobalProgressWidget';
import { captureSignupReferral } from '@/utils/signupReferral';
import { initAttribution, trackPageView } from '@/hooks/useAttribution';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { useAuth } from '@/hooks/useAuth';
import ProtectedRoute from '@/components/layout/ProtectedRoute';
import SupportChatWidget from '@/components/support-chat/SupportChatWidget';
import { LayoutBootstrap } from '@/components/providers/LayoutBootstrap';
import { EditorProviders } from '@/components/providers/EditorProviders';

// Lazy-loaded page components for code splitting
const Login = lazy(() => import('@/pages/Login'));
const WorkbookList = lazy(() => import('@/pages/WorkbookList'));
const WorkbookEditor = lazy(() => import('@/pages/WorkbookEditor'));
const Passages = lazy(() => import('@/pages/Passages'));
const LoadQuestions = lazy(() => import('@/pages/LoadQuestions'));
const Editor = lazy(() => import('@/pages/Editor'));
const SingleQuestion = lazy(() => import('@/pages/SingleQuestion'));
const OneClickMocktest = lazy(() => import('@/pages/OneClickMocktest'));
const Settings = lazy(() => import('@/pages/Settings'));
const OptionsSettingsTab = lazy(() => import('@/pages/Settings').then(m => ({ default: m.OptionsSettingsTab })));
const DocumentFormatTab = lazy(() => import('@/pages/Settings').then(m => ({ default: m.DocumentFormatTab })));
const OneClickFormatTab = lazy(() => import('@/pages/Settings').then(m => ({ default: m.OneClickFormatTab })));
const CopyQuestionPresetTab = lazy(() => import('@/pages/Settings').then(m => ({ default: m.CopyQuestionPresetTab })));
const UsageHistoryTab = lazy(() => import('@/pages/Settings').then(m => ({ default: m.UsageHistoryTab })));
const EditingOptionsTab = lazy(() => import('@/pages/Settings').then(m => ({ default: m.EditingOptionsTab })));
const LmsLinkTab = lazy(() => import('@/pages/Settings').then(m => ({ default: m.LmsLinkTab })));
const TrashTab = lazy(() => import('@/pages/Settings').then(m => ({ default: m.TrashTab })));
const Help = lazy(() => import('@/pages/Help'));
const OneClickMocktestHelp = lazy(() => import('@/pages/OneClickMocktestHelp'));
const SingleQuestionHelpTab = lazy(() => import('@/pages/Help').then(m => ({ default: m.SingleQuestionHelpTab })));
const WorkbookHelpTab = lazy(() => import('@/pages/WorkbookHelp'));
const ClassroHelp = lazy(() => import('@/pages/ClassroHelp'));
const EditorHelp = lazy(() => import('@/pages/EditorHelp'));
const VariationImportHelpTab = lazy(() => import('@/pages/Help').then(m => ({ default: m.VariationImportHelpTab })));
const PricingHelpTab = lazy(() => import('@/pages/Help').then(m => ({ default: m.PricingHelpTab })));
const QuestionTypesHelpTab = lazy(() => import('@/pages/Help').then(m => ({ default: m.QuestionTypesHelpTab })));
const NotFound = lazy(() => import('@/pages/NotFound'));

// Public pages (SPEC-MIGRATE-006)
const Store = lazy(() => import('@/pages/Store'));
const ExamFileStore = lazy(() => import('@/pages/ExamFileStore'));
const Membership = lazy(() => import('@/pages/Membership'));
const ProductDetail = lazy(() => import('@/pages/ProductDetail'));
const Checkout = lazy(() => import('@/pages/Checkout'));
const PaymentSuccess = lazy(() => import('@/pages/PaymentSuccess'));
const PaymentFail = lazy(() => import('@/pages/PaymentFail'));
const Register = lazy(() => import('@/pages/Register'));
const PhoneVerificationGate = lazy(() => import('@/pages/PhoneVerificationGate'));
const SocialRegister = lazy(() => import('@/pages/SocialRegister'));
const DesktopAuthComplete = lazy(() => import('@/pages/DesktopAuthComplete'));
const FindId = lazy(() => import('@/pages/FindId'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const Download = lazy(() => import('@/pages/Download'));
const ReleaseNotes = lazy(() => import('@/pages/ReleaseNotes'));
const AdminDownloads = lazy(() => import('@/pages/AdminDownloads'));
const AdminPopups = lazy(() => import('@/pages/AdminPopups'));
const AdminReleaseNotes = lazy(() => import('@/pages/AdminReleaseNotes'));
const Pricing = lazy(() => import('@/pages/Pricing'));
const QuestionTypes = lazy(() => import('@/pages/QuestionTypes'));
const ClassroIntegration = lazy(() => import('@/pages/ClassroIntegration'));
const Manual = lazy(() => import('@/pages/Manual'));
const Terms = lazy(() => import('@/pages/Terms'));
const TermsArchive20240126 = lazy(() => import('@/pages/terms-archive/Terms20240126'));
const TermsArchive20260325 = lazy(() => import('@/pages/terms-archive/Terms20260325'));
const TermsArchive20260514 = lazy(() => import('@/pages/terms-archive/Terms20260514'));
const Privacy = lazy(() => import('@/pages/Privacy'));
const MarketingConsent = lazy(() => import('@/pages/MarketingConsent'));

// Protected user pages (SPEC-MIGRATE-006)
const MyAccount = lazy(() => import('@/pages/MyAccount'));
const MyOrderDetail = lazy(() => import('@/pages/MyOrderDetail'));
const Board = lazy(() => import('@/pages/Board'));
const BoardPost = lazy(() => import('@/pages/BoardPost'));
const BoardWrite = lazy(() => import('@/pages/BoardWrite'));

// Admin pages (SPEC-MIGRATE-006)
const ResourceShare = lazy(() => import('@/pages/ResourceShare'));
const ResourceUnsubscribe = lazy(() => import('@/pages/ResourceShare').then(m => ({ default: m.ResourceUnsubscribe })));
const AdminResourceShares = lazy(() => import('@/pages/AdminResourceShares'));
const AdminSmsLogs = lazy(() => import('@/pages/AdminSmsLogs'));
const AdminUsers = lazy(() => import('@/pages/AdminUsers'));
const AdminEmailCampaigns = lazy(() => import('@/pages/AdminEmailCampaigns'));
const AdminUserDetail = lazy(() => import('@/pages/AdminUserDetail'));
const AdminOrders = lazy(() => import('@/pages/AdminOrders'));
const AdminRefundOrders = lazy(() => import('@/pages/AdminRefundOrders'));
const AdminDashboard = lazy(() => import('@/pages/AdminDashboard'));
const AdminSubscriptions = lazy(() => import('@/pages/AdminSubscriptions'));
const AdminProducts = lazy(() => import('@/pages/AdminProducts'));
const AdminProductEdit = lazy(() => import('@/pages/AdminProductEdit'));
const AdminOrderDetail = lazy(() => import('@/pages/AdminOrderDetail'));
const AdminAnalytics = lazy(() => import('@/pages/AdminAnalytics'));
const AdminUsageCalculation = lazy(() => import('@/pages/AdminUsageCalculation'));
const AdminReferrals = lazy(() => import('@/pages/AdminReferrals'));
const AdminBoardReviews = lazy(() => import('@/pages/AdminBoardReviews'));
const AdminSecurity = lazy(() => import('@/pages/AdminSecurity'));
const AdminNotificationSettings = lazy(() => import('@/pages/AdminNotificationSettings'));
const AdminErrorReports = lazy(() => import('@/pages/AdminErrorReports'));
const AdminSupportChat = lazy(() => import('@/pages/AdminSupportChat'));
const AdminCronLogs = lazy(() => import('@/pages/AdminCronLogs'));
const AdminMemory = lazy(() => import('@/pages/AdminMemory'));
const AdminSettings = lazy(() => import('@/pages/AdminSettings'));
const AdminPrompts = lazy(() => import('@/pages/AdminPrompts'));
const AdminGenerationConfig = lazy(() => import('@/pages/AdminGenerationConfig'));
const AdminPromptCache = lazy(() => import('@/pages/AdminPromptCache'));
const AdminFastCallCaps = lazy(() => import('@/pages/AdminFastCallCaps'));
const AdminCoupons = lazy(() => import('@/pages/AdminCoupons'));
const AdminLayout = lazy(() => import('@/components/layout/AdminLayout'));

// Track SPA page navigations for attribution session_pages
function PageViewTracker() {
  const location = useLocation();
  const isFirst = useRef(true);
  useEffect(() => {
    // Skip the initial mount (already counted by initAttribution)
    if (isFirst.current) { isFirst.current = false; return; }
    trackPageView();
  }, [location.pathname]);
  return null;
}

// Pages that should NOT redirect to phone verification
const PHONE_VERIFY_EXEMPT_PATHS = [
  '/login',
  '/verify-phone',
  '/register',
  '/social-register',
  '/find-id',
  '/forgot-password',
  '/reset-password',
  '/terms',
  '/privacy',
  '/share',
  '/admin',
];

// Global phone verification guard
// Redirects authenticated users with unverified phone to /verify-phone on ALL pages
function PhoneVerificationGuard({ children }: { children: ReactNode }) {
  const { state } = useAuth();
  const location = useLocation();

  const isExempt = PHONE_VERIFY_EXEMPT_PATHS.some(
    (path) => location.pathname === path || location.pathname.startsWith(path + '/')
  );

  const isAdmin = state.user?.role === 'administrator';

  if (
    !isExempt &&
    !isAdmin &&
    !state.isLoading &&
    state.isAuthenticated &&
    state.user &&
    state.user.is_phone_verified !== true
  ) {
    return <Navigate to="/verify-phone" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

const Home = lazy(() => import('@/pages/Home'));

// Loading fallback component
function PageLoader() {
  return (
    <div className="flex items-center justify-center h-screen">
      <div className="text-center">
        <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-[#F2CC8F] border-t-transparent" />
        <p className="mt-4 text-gray-600">Loading...</p>
      </div>
    </div>
  );
}

function AppChrome() {
  const { isImpersonating: ctxImpersonating } = useAuth();
  const tokenImpersonating = useImpersonationActive();
  // Match AdminModeBar so the reserved top padding never falls out of sync
  // with banner visibility (otherwise the banner overlaps content).
  const isImpersonating = ctxImpersonating || tokenImpersonating;
  const shellStyle = {
    '--impersonation-bar-height': isImpersonating ? '48px' : '0px',
  } as CSSProperties;

  // Mirror the banner state onto <html>/<body> so full-screen overlays can be
  // pushed below the banner even when they are rendered through a portal
  // (outside this subtree). See the `body.is-impersonating .fixed.inset-0`
  // rule in index.css.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--impersonation-bar-height', isImpersonating ? '48px' : '0px');
    document.body.classList.toggle('is-impersonating', isImpersonating);
    return () => {
      root.style.removeProperty('--impersonation-bar-height');
      document.body.classList.remove('is-impersonating');
    };
  }, [isImpersonating]);

  return (
    <div style={shellStyle}>
      <ToastProvider>
        <QuestionGenerationProvider>
          {/* Load public settings (passage limits) for unauthenticated pages.
              For authenticated layout/editor pages, LayoutBootstrap seeds from session-init. */}
          <SettingsInitializer />
          {/* Track SPA page navigations for attribution */}
          <PageViewTracker />
          {/* Global progress widget for question generation */}
          <GlobalProgressWidget />
          {/* Support chat widget (hidden on admin pages) */}
          <SupportChatWidget />

          {/* Admin impersonation bar - shown on ALL pages */}
          <AdminModeBar />
          {/* "New build deployed, please reload" notice - shown on ALL pages */}
          <NewVersionBanner />
          {/* One-time "receive news?" prompt for members without marketing consent */}
          <MarketingConsentPrompt />
          <SitePopup />

          {/* The bar is in normal flow (sticky), so no extra top padding is
              needed here — adding it would make the document taller than the
              viewport and let content scroll underneath the banner. */}
          <div>
            <PhoneVerificationGuard>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Login route - WordPress SSO integration (unprotected) */}
                  <Route path="/login" element={<Login />} />

                  {/* Single Question route - public but uses editor providers */}
                  <Route
                    path="/single-question"
                    element={
                      <LayoutBootstrap prefetchEditor>
                        <EditorProviders>
                          <SingleQuestion />
                        </EditorProviders>
                      </LayoutBootstrap>
                    }
                  />

                  {/* One-click mock exam: sample exam (ocr) -> passages + generate -> results. One page instance serves all three steps. */}
                  <Route path="/one-click-mocktest" element={<OneClickMocktestRedirect />} />
                  {['ocr', 'generate', 'results'].map((step) => (
                    <Route
                      key={step}
                      path={`/one-click-mocktest/${step}`}
                      element={
                        <ProtectedRoute>
                          <LayoutBootstrap>
                            <OneClickMocktest />
                          </LayoutBootstrap>
                        </ProtectedRoute>
                      }
                    />
                  ))}

                  {/* Redirect legacy /dashboard to /workbooks */}
                  <Route path="/dashboard" element={<Navigate to="/workbooks" replace />} />

                  {/* Workbooks list route - Table view of all workbooks (protected) */}
                  <Route
                    path="/workbooks"
                    element={
                      <ProtectedRoute>
                        <LayoutBootstrap prefetchEditor>
                          <EditorProviders>
                            <WorkbookList />
                          </EditorProviders>
                        </LayoutBootstrap>
                      </ProtectedRoute>
                    }
                  />

                  {/* Workbook editor route - Edit specific workbook (protected) */}
                  <Route
                    path="/workbook/:workbookId"
                    element={
                      <ProtectedRoute>
                        <LayoutBootstrap prefetchEditor>
                          <EditorProviders>
                            <WorkbookEditor />
                          </EditorProviders>
                        </LayoutBootstrap>
                      </ProtectedRoute>
                    }
                  />

                  {/* Passages route - Manage saved passages (protected) */}
                  <Route
                    path="/passages"
                    element={
                      <ProtectedRoute>
                        <LayoutBootstrap>
                          <Passages />
                        </LayoutBootstrap>
                      </ProtectedRoute>
                    }
                  />

                  {/* Load Questions route - Load questions from Excel files (protected) */}
                  <Route
                    path="/load-questions"
                    element={
                      <ProtectedRoute>
                        <LayoutBootstrap>
                          <LoadQuestions />
                        </LayoutBootstrap>
                      </ProtectedRoute>
                    }
                  />

                  {/* Editor route - Passage editor for text processing (protected) */}
                  <Route
                    path="/editor"
                    element={
                      <ProtectedRoute>
                        <LayoutBootstrap prefetchEditor>
                          <EditorProviders>
                            <Editor />
                          </EditorProviders>
                        </LayoutBootstrap>
                      </ProtectedRoute>
                    }
                  />

                  {/* Settings route - Manage option settings (protected) */}
                  <Route
                    path="/settings"
                    element={
                      <ProtectedRoute>
                        <LayoutBootstrap prefetchEditor>
                          <EditorProviders>
                            <Settings />
                          </EditorProviders>
                        </LayoutBootstrap>
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<Navigate to="/settings/options" replace />} />
                    <Route path="options" element={<OptionsSettingsTab />} />
                    <Route path="document-format" element={<DocumentFormatTab />} />
                    <Route path="oneclick-format" element={<OneClickFormatTab />} />
                    <Route path="copyquestion-presets" element={<CopyQuestionPresetTab />} />
                    <Route path="editing-options" element={<EditingOptionsTab />} />
                    <Route path="usage-history" element={<UsageHistoryTab />} />
                    <Route path="lms-link" element={<LmsLinkTab />} />
                    <Route path="trash" element={<TrashTab />} />
                  </Route>

                  {/* Help route - Help page (public, but Layout→Header→NotificationBell needs provider) */}
                  <Route
                    path="/help"
                    element={<LayoutBootstrap><Help /></LayoutBootstrap>}
                  >
                    <Route index element={<Navigate to="/help/single-question" replace />} />
                    <Route path="single-question" element={<SingleQuestionHelpTab />} />
                    <Route path="workbooks" element={<WorkbookHelpTab />} />
                    <Route path="classro" element={<ClassroHelp />} />
                    <Route path="editor" element={<EditorHelp />} />
                    <Route path="one-click-mocktest" element={<OneClickMocktestHelp />} />
                    <Route path="variation-import" element={<VariationImportHelpTab />} />
                    <Route path="pricing" element={<PricingHelpTab />} />
                    <Route path="types" element={<QuestionTypesHelpTab />} />
                  </Route>

                  {/* Public pages (SPEC-MIGRATE-006) */}
                  <Route path="/share/unsubscribe/:token" element={<ResourceUnsubscribe />} />
                  <Route path="/share/:slug" element={<ResourceShare />} />
                  <Route path="/store" element={<Store />} />
                  <Route path="/store/membership" element={<Membership />} />
                  <Route path="/store/exam-files" element={<ExamFileStore />} />
                  <Route path="/store/:id" element={<ProductDetail />} />
                  <Route path="/checkout" element={<Checkout />} />
                  <Route path="/payment/success" element={<PaymentSuccess />} />
                  <Route path="/payment/fail" element={<PaymentFail />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/verify-phone" element={<PhoneVerificationGate />} />
                  <Route path="/social-register" element={<SocialRegister />} />
                  <Route path="/desktop-auth-complete" element={<DesktopAuthComplete />} />
                  <Route path="/find-id" element={<FindId />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/download" element={<Download />} />
                  <Route path="/updates" element={<ReleaseNotes />} />
                  <Route path="/pricing" element={<Pricing />} />
                  <Route path="/question-types" element={<QuestionTypes />} />
                  <Route path="/classro" element={<ClassroIntegration />} />
                  <Route path="/manual" element={<Navigate to="/manual/basic-usage" replace />} />
                  <Route path="/manual/:section" element={<Manual />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/terms/archive/2024-01-26" element={<TermsArchive20240126 />} />
                  <Route path="/terms/archive/2026-03-25" element={<TermsArchive20260325 />} />
                  <Route path="/terms/archive/2026-05-14" element={<TermsArchive20260514 />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/marketing-consent" element={<MarketingConsent />} />

                  {/* Protected user pages (SPEC-MIGRATE-006) */}
                  <Route
                    path="/account"
                    element={
                      <ProtectedRoute>
                        <LayoutBootstrap>
                          <MyAccount />
                        </LayoutBootstrap>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/account/orders/:orderId"
                    element={
                      <ProtectedRoute>
                        <LayoutBootstrap>
                          <MyOrderDetail />
                        </LayoutBootstrap>
                      </ProtectedRoute>
                    }
                  />
                  {/* Board list and detail are public (no login required) */}
                  <Route path="/board/:type" element={<Board />} />
                  <Route path="/board/:type/:id" element={<BoardPost />} />
                  {/* Board write requires login */}
                  <Route
                    path="/board/:type/write"
                    element={
                      <ProtectedRoute>
                        <LayoutBootstrap>
                          <BoardWrite />
                        </LayoutBootstrap>
                      </ProtectedRoute>
                    }
                  />

                  {/* Admin pages (SPEC-MIGRATE-006, SPEC-ADMIN-001) */}
                  <Route path="/admin/dashboard" element={<Navigate to="/admin" replace />} />
                  <Route
                    path="/admin"
                    element={
                      <LayoutBootstrap>
                        <AdminLayout />
                      </LayoutBootstrap>
                    }
                  >
                    <Route index element={<AdminDashboard />} />
                    <Route path="users" element={<AdminUsers />} />
                    <Route path="sms-logs" element={<AdminSmsLogs />} />
                    <Route path="users/:userId" element={<AdminUserDetail />} />
                    <Route path="orders" element={<AdminOrders />} />
                    <Route path="orders/refunds" element={<AdminRefundOrders />} />
                    <Route path="orders/:orderId" element={<AdminOrderDetail />} />
                    <Route path="products/:productId" element={<AdminProductEdit />} />
                    <Route path="products" element={<AdminProducts />} />
                    <Route path="coupons" element={<AdminCoupons />} />
                    <Route path="subscriptions" element={<AdminSubscriptions />} />
                    <Route path="analytics" element={<AdminAnalytics />} />
                    <Route path="usage-calculation" element={<AdminUsageCalculation />} />
                    <Route path="referrals" element={<AdminReferrals />} />
                    <Route path="reviews" element={<AdminBoardReviews />} />
                    <Route path="security" element={<AdminSecurity />} />
                    <Route path="notification-settings" element={<AdminNotificationSettings />} />
                    <Route path="error-reports" element={<AdminErrorReports />} />
                    <Route path="resource-shares" element={<AdminResourceShares />} />
                    <Route path="email-campaigns" element={<AdminEmailCampaigns />} />
                    <Route path="support-chat" element={<AdminSupportChat />} />
                    <Route path="cron-logs" element={<AdminCronLogs />} />
                    <Route path="memory" element={<AdminMemory />} />
                    <Route path="settings" element={<AdminSettings />} />
                    <Route path="downloads" element={<AdminDownloads />} />
                    <Route path="popups" element={<AdminPopups />} />
                    <Route path="release-notes" element={<AdminReleaseNotes />} />
                    <Route path="prompts" element={<AdminPrompts />} />
                    <Route path="generation-config" element={<AdminGenerationConfig />} />
                    <Route path="prompt-cache" element={<AdminPromptCache />} />
                    <Route path="fast-call-caps" element={<AdminFastCallCaps />} />
                  </Route>

                  {/* Home route: always show landing page */}
                  <Route path="/" element={<Suspense fallback={<PageLoader />}><Home /></Suspense>} />

                  {/* 404 Not Found - Catch all unknown routes */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </PhoneVerificationGuard>
          </div>
        </QuestionGenerationProvider>
      </ToastProvider>
    </div>
  );
}

// Capture visitor attribution (referrer, UTM) on first page load
initAttribution();
captureSignupReferral();

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <AppChrome />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
